import {
  Card, DataTable, ErrorState, Grid, LoadingState, PageHeader, Stat, StatusBadge, useApi, useI18n,
  type TenantStatus,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTrialLabel } from '../trial';

/**
 * The platform dashboard (FR-PLT-02).
 *
 * Counts only. Nothing here reaches into a tenant's own records — that is the whole point of the platform
 * boundary, and the endpoint behind it aggregates rather than joining through to children.
 */
export function Dashboard() {
  const api = useApi();
  const { t, formatNumber } = useI18n();
  const trialLabel = useTrialLabel();

  const dashboard = useQuery({
    queryKey: ['platform', 'dashboard'],
    queryFn: () => api.platform.dashboard(),
  });

  if (dashboard.isPending) return <LoadingState />;
  if (dashboard.isError) {
    return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  }

  const data = dashboard.data;
  const byStatus = Object.entries(data.tenantsByStatus) as [TenantStatus, number][];
  const total = byStatus.reduce((sum, [, count]) => sum + count, 0);

  return (
    <>
      <PageHeader title={t('dashboard.title')} lede={t('dashboard.lede')} />

      <Grid>
        <Stat
          label={t('nav.tenants')}
          value={formatNumber(total)}
          note={t('dashboard.newTenants') + ': ' + formatNumber(data.newTenantsThisMonth)}
        />
        <Stat
          label={t('dashboard.activeChildren')}
          value={formatNumber(data.activeChildren)}
          note={t('dashboard.acrossPlatform')}
        />
        <Stat
          label={t('dashboard.activeStaff')}
          value={formatNumber(data.activeStaff)}
          note={t('dashboard.acrossPlatform')}
        />
        <Stat
          label={t('dashboard.supportSessions')}
          value={formatNumber(data.activeSupportSessions)}
          // A live support session is a platform employee inside a tenant's data. It is never routine, so
          // it is coloured as something to look at rather than as a neutral count (SRS §3.5).
          {...(data.activeSupportSessions > 0 ? { note: t('support.banner'), tone: 'warning' as const } : {})}
        />
      </Grid>

      {/* The trials someone has to decide about, so none runs out unnoticed (C6). */}
      <Card title={t('dashboard.trialsEnding')} hint={t('dashboard.trialsEnding.hint')} flush>
        <DataTable
          caption={t('dashboard.trialsEnding.caption')}
          rows={data.trialsEnding}
          rowKey={(row) => row.tenantId}
          empty={t('dashboard.trialsEnding.empty')}
          columns={[
            {
              key: 'name',
              header: t('tenants.column.name'),
              render: (row) => <Link to={`/tenants/${row.tenantId}`}>{row.nameAr}</Link>,
            },
            {
              key: 'lastDay',
              header: t('dashboard.trialsEnding.lastDay'),
              render: (row) => trialLabel(row.trialEndsAt, row.timeZone),
            },
          ]}
        />
      </Card>

      <Card title={t('dashboard.byStatus')}>
        <div className="dg-row">
          {byStatus.map(([status, count]) => (
            <span key={status} className="dg-row" style={{ gap: 'var(--dg-space-2)' }}>
              <StatusBadge status={status} />
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(count)}</strong>
            </span>
          ))}
        </div>
      </Card>
    </>
  );
}
